import React, { createContext, useContext, useMemo } from "react";

import useCheckPermissionsBatch from "@fartherfinance/frontend/api/Authz/hooks/useCheckPermissionBatch";
import {
  Attachment,
  TaskComment,
} from "@fartherfinance/frontend/api/Tasks/Types";
import { TaskId } from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

interface TaskPermissionsContextValue {
  commentPermissions: (comment: TaskComment) => {
    isAllowedToUpdate: boolean;
    isAllowedToDelete: boolean;
  };
  attachmentPermissions: (task: Attachment) => { isAllowedToDelete: boolean };
  taskPermissions: <T extends { id: TaskId }>(
    task: T
  ) => {
    isAllowedToUpdate: boolean;
    isAllowedToUpdateMetadata: boolean;
    isAllowedToDelete: boolean;
  };
}

const TaskPermissionsContext = createContext<
  TaskPermissionsContextValue | undefined
>(undefined);

export const TaskPermissionsProvider: React.FC<{
  comments: TaskComment[];
  attachments: Attachment[];
  taskId: TaskId;
  children: React.ReactNode;
}> = ({ comments, attachments, children, taskId }) => {
  const auth = useAdvisorRequestAuth();
  const canEditResults = useCheckPermissionsBatch(
    {
      action: "edit",
      resources: [
        ...comments.map((c) => ({ type: "TaskComment" as const, id: c.id })),
        { type: "Task", id: taskId },
      ],
    },
    auth
  );
  const canEditMetadataResults = useCheckPermissionsBatch(
    {
      action: "editMetadata",
      resources: [{ type: "Task", id: taskId }],
    },
    auth
  );
  const canDeleteResults = useCheckPermissionsBatch(
    {
      action: "delete",
      resources: [
        ...comments.map((c) => ({ type: "TaskComment" as const, id: c.id })),
        ...attachments.map((a) => ({ type: "Attachment" as const, id: a.id })),
        { type: "Task", id: taskId },
      ],
    },
    auth
  );

  const canEditMap = useMemo(
    () =>
      new Map<string, boolean>(
        canEditResults.data?.results.map(({ id }) => [id, true])
      ),
    [canEditResults]
  );

  const canEdiMetadataMap = useMemo(
    () =>
      new Map<string, boolean>(
        canEditMetadataResults.data?.results.map(({ id }) => [id, true])
      ),
    [canEditMetadataResults]
  );

  const canDeleteMap = useMemo(
    () =>
      new Map<string, boolean>(
        canDeleteResults.data?.results.map(({ id }) => [id, true])
      ),
    [canDeleteResults]
  );

  const value = useMemo(
    () => ({
      commentPermissions: (comment: TaskComment) => ({
        isAllowedToUpdate: canEditMap.has(comment.id),
        isAllowedToDelete: canDeleteMap.has(comment.id),
      }),
      attachmentPermissions: (attachment: Attachment) => ({
        isAllowedToDelete: canDeleteMap.has(attachment.id),
      }),
      taskPermissions: <T extends { id: TaskId }>(task: T) => ({
        isAllowedToUpdate: canEditMap.has(task.id),
        isAllowedToUpdateMetadata: canEdiMetadataMap.has(task.id),
        isAllowedToDelete: canDeleteMap.has(task.id),
      }),
    }),
    [canDeleteMap, canEditMap, canEdiMetadataMap]
  );

  return (
    <TaskPermissionsContext.Provider value={value}>
      {children}
    </TaskPermissionsContext.Provider>
  );
};

export const useTaskPermissions = () => {
  const context = useContext(TaskPermissionsContext);
  if (context === undefined) {
    throw new Error(
      "useTaskPermissions must be used within the TaskPermissionsContext"
    );
  }
  return context;
};
